import { getWeather, type TripWeather } from '@trippath/shared';
import { useEffect, useState } from 'react';

/** How long a trip's weather is kept on the page before it is asked again: the server keeps
 * the forecast three hours, the usual weather for good. */
const KEPT_MS = 10 * 60 * 1000;

const kept = new Map<string, { at: number; answer: Promise<TripWeather> }>();

/** A trip's weather, asked once for every part of the page that shows it. */
export function weatherOf(trip: string, now = Date.now()): Promise<TripWeather> {
  const known = kept.get(trip);
  if (known && now - known.at < KEPT_MS) return known.answer;
  const answer = getWeather(trip);
  kept.set(trip, { at: now, answer });
  answer.catch(() => {
    kept.delete(trip);
  });
  return answer;
}

/** Forgets every trip's weather (a test starts afresh). */
export function forgetWeather() {
  kept.clear();
}

export type WeatherState =
  { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; weather: TripWeather };

/** The weather once it is had; none while it loads or when it failed. */
export function readyWeather(state: WeatherState): TripWeather | null {
  return state.kind === 'ready' ? state.weather : null;
}

/** A trip's weather for a component, loading until it is had. */
export function useWeather(trip: string): WeatherState {
  const [state, setState] = useState<WeatherState>({ kind: 'loading' });
  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    weatherOf(trip).then(
      (weather) => {
        if (live) setState({ kind: 'ready', weather });
      },
      () => {
        if (live) setState({ kind: 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [trip]);
  return state;
}
